import React, { useMemo } from "react";
import Select from "react-select";

import { getSelectFieldStyles, SelectFieldOption } from "components/global/select-field-styles";
import { FieldErrorText, PrimaryActionButton } from "pages/app/workspace/sections/styles/workspace-section.styles";
import {
  TeamRosterAddDescription,
  TeamRosterAddEmptyText,
  TeamRosterAddFieldLabel,
  TeamRosterAddHeader,
  TeamRosterAddPanel,
  TeamRosterAddScopeChip,
  TeamRosterAddScopeRow,
  TeamRosterAddSelectField,
  TeamRosterAddTitle,
  TeamRosterAddToolbar,
} from "pages/app/workspace/sections/styles/school-team-section-forms.inline.styles";

type TeamPlayerAssignmentFormProps = {
  assignablePlayers: Array<{
    full_name?: string;
    id: string;
    school_grade?: string;
  }>;
  emptyState?: "all-on-roster" | "no-school-players" | "no-sport-registration";
  isLoadingPlayers?: boolean;
  isSubmitting: boolean;
  onPlayerChange: (value: string) => void;
  onSubmit: () => void;
  schoolName?: string;
  selectedPlayerError?: string;
  selectedPlayerId: string;
  sportName?: string;
  teamName?: string;
};

const buildPlayerOptionLabel = (player: TeamPlayerAssignmentFormProps["assignablePlayers"][number]) => {
  const name = player.full_name || "Unknown player";
  return player.school_grade ? `${name} · ${player.school_grade}` : name;
};

export const TeamPlayerAssignmentForm: React.FC<TeamPlayerAssignmentFormProps> = ({
  assignablePlayers,
  emptyState,
  isLoadingPlayers = false,
  isSubmitting,
  onPlayerChange,
  onSubmit,
  schoolName,
  selectedPlayerError,
  selectedPlayerId,
  sportName,
  teamName,
}) => {
  const playerOptions = useMemo<SelectFieldOption[]>(
    () =>
      assignablePlayers.map((player) => ({
        label: buildPlayerOptionLabel(player),
        value: String(player.id),
      })),
    [assignablePlayers],
  );
  const selectedPlayerOption =
    playerOptions.find((option) => option.value === selectedPlayerId) || null;
  const playerSelectStyles = useMemo(
    () => getSelectFieldStyles(Boolean(selectedPlayerError)),
    [selectedPlayerError],
  );
  const hasAssignablePlayers = assignablePlayers.length > 0;
  const hasScopeChips = Boolean(teamName || schoolName || sportName);

  const emptyStateMessage =
    emptyState === "no-school-players"
      ? "No school players yet. Add them from People first."
      : emptyState === "no-sport-registration"
        ? sportName
          ? `No ${sportName} players enrolled yet. Add them under Programmes.`
          : "No players enrolled for this sport yet. Add them under Programmes."
        : "Everyone eligible is already on this squad.";

  return (
    <TeamRosterAddPanel>
      <TeamRosterAddHeader>
        <TeamRosterAddTitle>Add player to roster</TeamRosterAddTitle>
        {hasScopeChips ? (
          <TeamRosterAddScopeRow>
            {teamName ? (
              <TeamRosterAddScopeChip $tone="team" title="Team">
                {teamName}
              </TeamRosterAddScopeChip>
            ) : null}
            {schoolName ? (
              <TeamRosterAddScopeChip $tone="school" title="School">
                {schoolName}
              </TeamRosterAddScopeChip>
            ) : null}
            {sportName ? (
              <TeamRosterAddScopeChip $tone="sport" title="Sport">
                {sportName}
              </TeamRosterAddScopeChip>
            ) : null}
          </TeamRosterAddScopeRow>
        ) : null}
        <TeamRosterAddDescription>
          Showing eligible players not already on this squad.
        </TeamRosterAddDescription>
      </TeamRosterAddHeader>

      {isLoadingPlayers ? (
        <TeamRosterAddEmptyText>Loading eligible school players...</TeamRosterAddEmptyText>
      ) : hasAssignablePlayers ? (
        <TeamRosterAddToolbar>
          <TeamRosterAddSelectField>
            <TeamRosterAddFieldLabel htmlFor="team-roster-player-select">Player</TeamRosterAddFieldLabel>
            <Select
              aria-invalid={Boolean(selectedPlayerError)}
              classNamePrefix="team-roster-player-select"
              inputId="team-roster-player-select"
              isClearable
              isDisabled={isSubmitting}
              isLoading={isLoadingPlayers}
              isSearchable
              menuPlacement="auto"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              onChange={(option) => onPlayerChange(option?.value ?? "")}
              options={playerOptions}
              placeholder="Search school players"
              styles={playerSelectStyles}
              value={selectedPlayerOption}
            />
            {selectedPlayerError ? <FieldErrorText>{selectedPlayerError}</FieldErrorText> : null}
          </TeamRosterAddSelectField>

          <PrimaryActionButton
            disabled={!selectedPlayerId || isSubmitting || isLoadingPlayers}
            onClick={onSubmit}
            type="button"
          >
            {isSubmitting ? "Adding..." : "Add to roster"}
          </PrimaryActionButton>
        </TeamRosterAddToolbar>
      ) : (
        <TeamRosterAddEmptyText>{emptyStateMessage}</TeamRosterAddEmptyText>
      )}
    </TeamRosterAddPanel>
  );
};
